import { useEffect, useMemo, useState } from "react";
import { Edit3, KeyRound, Plus, Trash2, Unlock } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { DataTable, type Column } from "../../components/DataTable/DataTable";
import { FilterBar } from "../../components/FilterBar/FilterBar";
import { StatusPill } from "../../components/StatusPill/StatusPill";
import { TopNav } from "../../components/TopNav/TopNav";
import { type UserRow, api } from "../../lib/api";
import { authStorage } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import {
  fullName,
  getRole,
  getStatus,
  roleLabel,
  statusLabel,
  type UserRoleValue,
  type UserStatus,
} from "../../lib/users";

import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { PasswordDialog } from "./PasswordDialog";
import { UserFormDialog } from "./UserFormDialog";

import styles from "./AdminPage.module.css";

export default function AdminPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const [formOpen, setFormOpen] = useState(false);
  const [formUser, setFormUser] = useState<UserRow | null>(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState<UserRow | null>(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delUser, setDelUser] = useState<UserRow | null>(null);
  const [delSubmitting, setDelSubmitting] = useState(false);

  const me = authStorage.getUser();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listUsers();
      setUsers(result.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os utilizadores.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function openCreate() {
    setFormUser(null);
    setFormOpen(true);
  }

  function openEdit(user: UserRow) {
    setFormUser(user);
    setFormOpen(true);
  }

  function openPassword(user: UserRow) {
    setPwUser(user);
    setPwOpen(true);
  }

  function openDelete(user: UserRow) {
    setDelUser(user);
    setDelOpen(true);
  }

  async function handleDelete() {
    if (!delUser) return;
    setDelSubmitting(true);
    try {
      await api.deleteUser(delUser.id);
      setDelOpen(false);
      setDelUser(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível apagar o utilizador.");
    } finally {
      setDelSubmitting(false);
    }
  }

  async function handleUnlock(user: UserRow) {
    setError(null);
    try {
      await api.unlockUser(user.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível desbloquear a conta.");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q) {
        const haystack = `${u.username} ${u.email} ${u.first_name} ${u.last_name}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (roleFilter && getRole(u) !== (roleFilter as UserRoleValue)) return false;
      if (statusFilter && getStatus(u) !== (statusFilter as UserStatus)) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const columns: Column<UserRow>[] = [
    {
      key: "name",
      header: "Nome",
      render: (row) => (
        <div className={styles.nameCell}>
          <span className={styles.avatar} aria-hidden="true">
            {(row.first_name || row.username).slice(0, 1).toUpperCase()}
          </span>
          <div>
            <div className={styles.fullName}>{fullName(row)}</div>
            <div className={styles.username}>@{row.username}</div>
          </div>
        </div>
      ),
      sortable: true,
      sortValue: (row) => fullName(row).toLowerCase(),
    },
    {
      key: "email",
      header: "Email",
      render: (row) => row.email || "—",
      sortable: true,
      sortValue: (row) => row.email,
    },
    {
      key: "role",
      header: "Função",
      render: (row) => roleLabel(getRole(row)),
      width: "150px",
      sortable: true,
      sortValue: (row) => roleLabel(getRole(row)),
    },
    {
      key: "status",
      header: "Estado",
      render: (row) => {
        const s = getStatus(row);
        if (s === "active") return <StatusPill tone="sent">{statusLabel(s)}</StatusPill>;
        if (s === "locked") return <StatusPill tone="to-send">{statusLabel(s)}</StatusPill>;
        return <StatusPill tone="neutral">{statusLabel(s)}</StatusPill>;
      },
      width: "120px",
      sortable: true,
      sortValue: (row) => getStatus(row),
    },
    {
      key: "last_login",
      header: "Último acesso",
      render: (row) => formatDateTime(row.last_login),
      width: "170px",
      sortable: true,
      sortValue: (row) => (row.last_login ? new Date(row.last_login) : null),
    },
    {
      key: "actions",
      header: "Ações",
      width: "180px",
      render: (row) => {
        const isMe = me?.id === row.id;
        const status = getStatus(row);
        return (
          <div className={styles.actions}>
            {status === "locked" && (
              <button
                className={styles.actionBtn}
                onClick={() => handleUnlock(row)}
                aria-label="Desbloquear"
                title="Desbloquear"
              >
                <Unlock size={14} />
              </button>
            )}
            <button
              className={styles.actionBtn}
              onClick={() => openEdit(row)}
              aria-label="Editar"
              title="Editar"
            >
              <Edit3 size={14} />
            </button>
            <button
              className={styles.actionBtn}
              onClick={() => openPassword(row)}
              aria-label="Mudar palavra-passe"
              title="Mudar palavra-passe"
            >
              <KeyRound size={14} />
            </button>
            <button
              className={`${styles.actionBtn} ${styles.actionDanger}`}
              onClick={() => openDelete(row)}
              aria-label="Apagar"
              title={isMe ? "Não podes apagar a tua própria conta" : "Apagar"}
              disabled={isMe}
            >
              <Trash2 size={14} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.page}>
      <TopNav />

      <main className={styles.main}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <Card title="Utilizadores">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar por nome ou email…"
            estadoValue={statusFilter}
            onEstadoChange={setStatusFilter}
            estadoOptions={[
              { value: "active", label: "Activo" },
              { value: "locked", label: "Bloqueado" },
              { value: "inactive", label: "Inactivo" },
            ]}
            onApply={() => {}}
            onClear={() => {
              setSearch("");
              setRoleFilter("");
              setStatusFilter("");
            }}
            trailing={
              <>
                <select
                  className={styles.roleSelect}
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  aria-label="Função"
                >
                  <option value="">Todas as funções</option>
                  <option value="admin">Administrador</option>
                  <option value="staff">Operador</option>
                  <option value="user">Utilizador</option>
                </select>
                <Button variant="primary" onClick={openCreate}>
                  <Plus size={14} /> Criar utilizador
                </Button>
              </>
            }
          />
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            empty={loading ? "A carregar…" : "Sem utilizadores."}
          />
        </Card>
      </main>

      <UserFormDialog
        open={formOpen}
        user={formUser}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          void refresh();
        }}
      />
      <PasswordDialog
        open={pwOpen}
        user={pwUser}
        onClose={() => setPwOpen(false)}
        onSaved={() => {
          setPwOpen(false);
          void refresh();
        }}
      />
      <ConfirmDeleteDialog
        open={delOpen}
        user={delUser}
        onClose={() => setDelOpen(false)}
        onConfirm={handleDelete}
        submitting={delSubmitting}
      />
    </div>
  );
}
